// src/shared/services/use-vaccination-search.ts
// Encounter vaccine search (lot-aware admin popup, PROD-1227).
//   GET /api/Master/SearchValueSets2?SearchText={q}&SearchFor=Vaccination
// Returns DDC-coded vaccines (system "DDC", NOT CVX) with route + unit-of-measurement embedded —
// so Route and the administration/dosage Units derive from the selected vaccine, not separate
// master endpoints. The vaccine's `code` (DDC) is what the LotPicker passes to
// `Items/ByCode/{code}/Lots`, and what must match the inventory StockItem's Code/ClinicalRef for
// the deduction to resolve. (curl-verified by Andrew 2026-06-18.) Shared so both the encounter
// popup and any future surface use one source.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

export interface VaccineCoding {
  code: string;
  display: string;
  system: string; // "DDC"
}
export interface VaccineRoute {
  code: string;
  display: string;
  system: string;
}
/** One row of SearchValueSets2?SearchFor=Vaccination — vaccine + its route(s) + unit. */
export interface VaccineValueSet {
  valueCoding: VaccineCoding;
  unitOfMeasurement: string | null; // e.g. "ML" — used for both admin + dose unit
  route: VaccineRoute[];            // 1 → fixed/disabled; >1 → dropdown (Flutter parity)
}

const MIN_QUERY_LENGTH = 1;

export function useVaccinationSearch(searchText: string, enabled = true) {
  const q = searchText.trim();
  return useQuery<VaccineValueSet[]>({
    queryKey: ['master-vaccination-valuesets', q],
    enabled: enabled && q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ SearchText: q, SearchFor: 'Vaccination' });
      const res = await get<VaccineValueSet[]>(
        `/api/Master/SearchValueSets2?${qs.toString()}`,
        { signal },
      );
      return res ?? [];
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
